import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useUser } from '../../context/UserContext';
import { useI18n } from '../../i18n/I18nContext';
import {
    Container,
    Card,
    Table,
    Button,
    Badge,
    Spinner,
    Alert,
    Dropdown,
    Tooltip,
    OverlayTrigger,
    Modal,
    Form
} from 'react-bootstrap';
import { FaPlus, FaFilePdf, FaEye, FaDownload, FaCloudUploadAlt, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import invoiceService from '../../services/invoiceService';

const InvoiceList = () => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { user } = useUser();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [showAvoirModal, setShowAvoirModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedMissions, setSelectedMissions] = useState([]);
    const [missionAmounts, setMissionAmounts] = useState({});
    const [showPdpModal, setShowPdpModal] = useState(false);
    const [pdpModalMessage, setPdpModalMessage] = useState('');

    useEffect(() => {
        if (user) {
            fetchInvoices();
        }
    }, [user]);

    const fetchInvoices = async () => {
        try {
            const data = await invoiceService.getAllInvoices(user.id || user.transporteurId);
            setInvoices(data);
        } catch (err) {
            console.error(err);
            toast.error(t('billing.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (id, number) => {
        try {
            const blob = await invoiceService.downloadPdf(id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Facture_${number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error(err);
            toast.error(t('billing.errors.downloadFailed'));
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await invoiceService.updateStatus(id, newStatus);
            toast.success("Le statut a été mis à jour avec succès.");
            fetchInvoices();
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la mise à jour du statut.");
        }
    };

    const handlePdpSubmit = async (id) => {
        setActionLoading(id);
        try {
            await invoiceService.submitToPdp(id);
            toast.success(t('billing.pdp.success'));
            fetchInvoices();
        } catch (err) {
            console.error(err);
            if (err.response?.status === 503) {
                setPdpModalMessage(err.response?.data?.message || "Le PDP n'est pas configuré.");
                setShowPdpModal(true);
            } else {
                toast.error(err.response?.data?.message || err.message || t('billing.pdp.error'));
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handlePdpRefresh = async (id) => {
        setActionLoading(id);
        try {
            await invoiceService.refreshPdpStatus(id);
            toast.success(t('billing.pdp.refreshSuccess'));
            fetchInvoices();
        } catch (err) {
            console.error(err);
            if (err.response?.status === 503) {
                setPdpModalMessage(err.response?.data?.message || "Le PDP n'est pas configuré.");
                setShowPdpModal(true);
            } else {
                toast.error(err.response?.data?.message || err.message || "Erreur lors du rafraîchissement");
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateTotalCreditNote = async (id) => {
        if (!window.confirm("CONFIRMATION : Voulez-vous générer un avoir total pour annuler l'intégralité de cette facture ? Cette action est irréversible.")) return;
        
        setActionLoading(id);
        try {
            await invoiceService.createCreditNote(id, []); // Send empty list for total
            toast.success("L'avoir total a été généré avec succès.");
            fetchInvoices();
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la création de l'avoir total.");
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateCreditNote = async (id) => {
        setActionLoading(id);
        try {
            const fullInvoice = await invoiceService.getInvoice(id);
            setSelectedInvoice(fullInvoice);
            setSelectedMissions(fullInvoice.missions.filter(m => !m.alreadyCredited).map(m => m.id));
            
            // Initialize amounts with original mission prices
            const initialAmounts = {};
            fullInvoice.missions.forEach(m => {
                initialAmounts[m.id] = m.price;
            });
            setMissionAmounts(initialAmounts);
            
            setShowAvoirModal(true);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de la récupération des détails de la facture.");
        } finally {
            setActionLoading(null);
        }
    };

    const submitAvoir = async () => {
        if (selectedMissions.length === 0) {
            toast.warning("Veuillez sélectionner au moins une mission.");
            return;
        }

        setActionLoading(selectedInvoice.id);
        try {
            const payload = {
                lines: selectedMissions.map(id => ({
                    missionId: id,
                    amountHt: missionAmounts[id]
                }))
            };
            await invoiceService.createCreditNote(selectedInvoice.id, payload);
            toast.success("L'avoir a été créé avec succès.");
            setShowAvoirModal(false);
            fetchInvoices();
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.message || err.message || "Erreur lors de la création de l'avoir.";
            toast.error(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const toggleMissionSelection = (id) => {
        setSelectedMissions(prev => 
            prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
        );
    };

    const getPdpBadgeColor = (status) => {
        switch (status) {
            case 'ACCEPTED':
            case 'VALIDATED':
            case 'ACCEPTED_BY_PDP': return 'success';
            case 'SUBMITTED':
            case 'SUBMITTED_TO_PDP': return 'info';
            case 'REJECTED':
            case 'ERROR':
            case 'REJECTED_BY_PDP': return 'danger';
            default: return 'secondary';
        }
    };

    const isFrenchUser = user?.country?.toLowerCase() === 'france' || user?.country?.toLowerCase() === 'fr';

    return (
        <DashboardLayout>
            <ToastContainer />
            <Container className="py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold text-primary">{t('billing.title')}</h2>
                        <p className="text-muted">{t('billing.subtitle')}</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button 
                            variant="outline-primary" 
                            className="rounded-pill px-4 d-flex align-items-center" 
                            onClick={() => navigate('/billing/automatic')}
                            style={{ borderWidth: '2px', fontWeight: '600' }}
                        >
                            <FaSync className="me-2" /> {t('billing.automaticInvoiceBtn')}
                        </Button>
                        <Button variant="primary" className="rounded-pill px-4" onClick={() => navigate('/billing/create')}>
                            <FaPlus className="me-2" /> {t('billing.newBtn')}
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : invoices.length === 0 ? (
                    <Card className="text-center py-5 border-0 shadow-sm rounded-4">
                        <div className="mb-3 text-muted">
                            <FaFilePdf size={48} className="opacity-25" />
                        </div>
                        <h4>{t('billing.noInvoices')}</h4>
                        <p className="text-muted">{t('billing.noInvoicesDesc')}</p>
                    </Card>
                ) : (
                    <Card className="border-0 shadow-sm rounded-4" style={{ minHeight: '400px' }}>
                        <div style={{ overflow: 'visible' }}>
                            <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="py-3 ps-4">{t('billing.table.number')}</th>
                                    <th>Type</th>
                                    <th>{t('billing.table.client')}</th>
                                    <th>{t('billing.table.date')}</th>
                                    <th>{t('billing.table.amountTtc')}</th>
                                    <th>Solde</th>
                                    <th>{t('billing.table.status')}</th>
                                    <th className="text-end pe-4">{t('billing.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map(inv => (
                                    <tr key={inv.id}>
                                        <td className="ps-4 fw-bold text-primary">{inv.invoiceNumber}</td>
                                        <td>
                                            <Badge bg={inv.type === 'CREDIT_NOTE' ? 'warning' : 'light'} text={inv.type === 'CREDIT_NOTE' ? 'dark' : 'dark'} className="border">
                                                {inv.type === 'CREDIT_NOTE' ? 'Avoir' : 'Facture'}
                                            </Badge>
                                        </td>
                                        <td>{inv.clientName}</td>
                                        <td>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                        <td className={`fw-bold ${inv.type === 'CREDIT_NOTE' ? 'text-danger' : ''}`}>
                                            {inv.type === 'CREDIT_NOTE' ? '-' : ''}{inv.totalTtc} {inv.currency}
                                        </td>
                                        <td className="fw-bold">{inv.type === 'INVOICE' ? `${inv.solde} ${inv.currency}` : '-'}</td>
                                        <td>
                                            <Badge bg={inv.status === 'PAID' ? 'success' : inv.status === 'SENT' ? 'info' : inv.status === 'CANCELLED_AVOIR' ? 'danger' : 'secondary'}>
                                                {inv.status === 'CANCELLED_AVOIR' ? 'Annulée par avoir' : t(`billing.statuses.${inv.status}`, { defaultValue: inv.status })}
                                            </Badge>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex justify-content-end align-items-center gap-2">

                                                {/* PDP button + statut groupés (only for France) */}
                                                {isFrenchUser && (
                                                    <div className="d-flex align-items-center gap-1 border rounded-pill px-2 py-1" style={{ backgroundColor: '#f8f9fa' }}>
                                                        {!inv.pdpStatus || inv.pdpStatus === 'ERROR' || inv.pdpStatus === 'REJECTED' || inv.status === 'REJECTED_BY_PDP' ? (
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                className="rounded-pill px-2 border-0"
                                                                onClick={() => handlePdpSubmit(inv.id)}
                                                                disabled={actionLoading === inv.id}
                                                                title={t('billing.pdp.submit')}
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                {actionLoading === inv.id ? <Spinner size="sm" /> : <><FaCloudUploadAlt className="me-1" />PDP</>}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                className="rounded-circle p-1 border-0"
                                                                onClick={() => handlePdpRefresh(inv.id)}
                                                                disabled={actionLoading === inv.id}
                                                                title={t('billing.pdp.refresh')}
                                                            >
                                                                <FaSync size={11} className={actionLoading === inv.id ? 'fa-spin' : ''} />
                                                            </Button>
                                                        )}
                                                        {/* Statut PDP inline */}
                                                        {inv.pdpStatus ? (
                                                            <>
                                                                <Badge bg={getPdpBadgeColor(inv.pdpStatus)} style={{ fontSize: '0.65rem' }}>
                                                                    {inv.pdpStatus === 'SUBMITTED' ? 'En attente'
                                                                        : inv.pdpStatus === 'ACCEPTED' || inv.pdpStatus === 'VALIDATED' || inv.pdpStatus === 'ACCEPTED_BY_PDP' ? 'Acceptée'
                                                                        : inv.pdpStatus === 'REJECTED' || inv.pdpStatus === 'REJECTED_BY_PDP' ? 'Rejetée'
                                                                        : inv.pdpStatus === 'ERROR' ? 'Erreur'
                                                                        : inv.pdpStatus}
                                                                </Badge>
                                                                {inv.pdpErrorMessage && (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>{inv.pdpErrorMessage}</Tooltip>}
                                                                    >
                                                                        <span className="text-danger" style={{ cursor: 'pointer' }}>
                                                                            <FaExclamationTriangle size={11} />
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>Non soumis</span>
                                                        )}
                                                    </div>
                                                )}

                                                <Dropdown className="d-inline-block">
                                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="rounded-pill p-1 px-3">
                                                        {t('common.statusLabel')}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu align="end">
                                                        <Dropdown.Item onClick={() => handleStatusChange(inv.id, 'DRAFT')}>Brouillon</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleStatusChange(inv.id, 'SENT')}>Envoyée</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleStatusChange(inv.id, 'PAID')}>Payée</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleStatusChange(inv.id, 'OVERDUE')}>En retard</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handleStatusChange(inv.id, 'CANCELLED')}>Annulée</Dropdown.Item>
                                                        {inv.type !== 'CREDIT_NOTE' && inv.status !== 'CANCELLED_AVOIR' && (
                                                            <>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item 
                                                                    onClick={() => handleCreateCreditNote(inv.id)}
                                                                    disabled={inv.status === 'DRAFT'}
                                                                >
                                                                    <FaSync className="me-2" /> Avoir Partiel (Sélection)
                                                                </Dropdown.Item>
                                                            </>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="rounded-circle p-2"
                                                    onClick={() => handleDownload(inv.id, inv.invoiceNumber)}
                                                    title={t('billing.tooltips.downloadPdf')}
                                                >
                                                    <FaDownload />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )
            }
            </Container >

            {/* Modal de sélection des missions pour l'Avoir */}

            {/* Modal d'information PDP */}
            <Modal show={showPdpModal} onHide={() => setShowPdpModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                </Modal.Header>
                <Modal.Body className="text-center pb-5 pt-3">
                    <div className="mb-4">
                        <FaCloudUploadAlt size={64} className="text-secondary opacity-50" />
                    </div>
                    <h4 className="fw-bold mb-3">Service Indisponible</h4>
                    <p className="text-muted mb-4 px-3">
                        {pdpModalMessage}
                    </p>
                    <Button variant="primary" className="rounded-pill px-4" onClick={() => setShowPdpModal(false)}>
                        Compris
                    </Button>
                </Modal.Body>
            </Modal>
            <Modal show={showAvoirModal} onHide={() => setShowAvoirModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold text-primary">
                        <FaSync className="me-2" /> Créer un Avoir
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="d-flex align-items-center">
                        <FaExclamationTriangle className="me-2" />
                        <div>
                            Sélectionnez les missions à créditer pour la facture <strong>{selectedInvoice?.invoiceNumber}</strong>.
                        </div>
                    </Alert>
                    
                    <h5 className="mb-3">Missions de la facture</h5>
                    <Table responsive hover className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th width="40"><Form.Check 
                                    checked={selectedMissions.length === selectedInvoice?.missions.filter(m => !m.alreadyCredited).length}
                                    onChange={(e) => {
                                        if (e.target.checked) setSelectedMissions(selectedInvoice.missions.filter(m => !m.alreadyCredited).map(m => m.id));
                                        else setSelectedMissions([]);
                                    }}
                                /></th>
                                <th>Référence</th>
                                <th>Trajet</th>
                                <th className="text-end">Prix Initial (HT)</th>
                                <th className="text-end" width="150">Montant à créditer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedInvoice?.missions.map(m => (
                                <tr key={m.id} 
                                    className={m.alreadyCredited ? 'table-secondary' : ''}
                                >
                                    <td>
                                        <Form.Check 
                                            checked={selectedMissions.includes(m.id)}
                                            onChange={() => toggleMissionSelection(m.id)}
                                            disabled={m.alreadyCredited}
                                        />
                                    </td>
                                    <td className="fw-bold">
                                        {m.referenceNumber}
                                        {m.alreadyCredited && <Badge bg="secondary" className="ms-2">Déjà crédité</Badge>}
                                    </td>
                                    <td className="small text-muted">{m.departureLocation} → {m.arrivalLocation}</td>
                                    <td className="text-end text-muted">{m.price} {selectedInvoice.currency}</td>
                                    <td className="text-end">
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            value={missionAmounts[m.id] || ''}
                                            onChange={(e) => {
                                                const val = parseFloat(e.target.value);
                                                setMissionAmounts(prev => ({...prev, [m.id]: isNaN(val) ? 0 : val}));
                                                if (!selectedMissions.includes(m.id) && !isNaN(val) && val > 0) {
                                                    setSelectedMissions(prev => [...prev, m.id]);
                                                }
                                            }}
                                            disabled={m.alreadyCredited || !selectedMissions.includes(m.id)}
                                            min="0"
                                            max={m.price}
                                            step="0.01"
                                            className="text-end fw-bold"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="table-light">
                            <tr>
                                <td colSpan="4" className="text-end fw-bold">Total Brut à Créditer (HT) :</td>
                                <td className="text-end fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                                    {Object.keys(missionAmounts)
                                        .filter(id => selectedMissions.includes(id))
                                        .reduce((sum, id) => sum + (parseFloat(missionAmounts[id]) || 0), 0)
                                        .toFixed(2)} {selectedInvoice?.currency}
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </Modal.Body>
                <Modal.Footer className="bg-light border-0">
                    <Button variant="outline-secondary" onClick={() => setShowAvoirModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" className="px-4 rounded-pill" onClick={submitAvoir} disabled={actionLoading === selectedInvoice?.id}>
                        {actionLoading === selectedInvoice?.id ? <Spinner size="sm" className="me-2" /> : <FaSync className="me-2" />}
                        Générer l'Avoir ({selectedMissions.length} mission{selectedMissions.length > 1 ? 's' : ''})
                    </Button>
                </Modal.Footer>
            </Modal>
        </DashboardLayout >
    );
};

export default InvoiceList;

